import React, { useState, useEffect, useContext } from 'react';
import { CloseRounded, AddRounded, CheckRounded, KeyboardArrowDownRounded } from '@mui/icons-material';
import { Collapse } from 'antd';
import Button from '@common/Button';
import Tooltip from '@common/Tooltip';
import Tag from '@common/Tag';
import CircularLoader from '@common/CircularLoader';
import { UserContext } from '@context/UserContext';
import axios from '@common/axios';

const getConceptTags = (conceptId, conceptUsageMap, selectedLessonId) => {
    const lessons = conceptUsageMap?.[String(conceptId)];
    if (!lessons?.length) return [];
    const inCurrent = lessons.some(l => l.lessonId === selectedLessonId);
    const others = lessons.filter(l => l.lessonId !== selectedLessonId);
    const tags = [];
    if (inCurrent) tags.push({ color: 'green', text: 'This lesson' });
    if (others.length === 1) tags.push({ color: 'default', text: `Lesson ${others[0].lessonNumber}` });
    else if (others.length >= 2) tags.push({ color: 'default', text: `${others.length}${inCurrent ? ' more' : ''} lessons` });
    return tags;
};

const AddContentPanel = ({ open, onClose, selectedLessonContent, selectedLessonId, conceptUsageMap, onAdd }) => {
    const { user } = useContext(UserContext);
    const [topicGroups, setTopicGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [justAddedIds, setJustAddedIds] = useState(new Set());

    useEffect(() => {
        if (!user?.syllabusId) return;
        axios.get(`/web/ui/nav/syllabus/${user.syllabusId}/topicGroups`)
            .then(r => {
                setTopicGroups(r.data);
                setIsLoading(false);
            });
    }, [user?.syllabusId]);

    const addedIds = new Set((selectedLessonContent || []).map(c => c.id));

    const collapseItems = topicGroups.flatMap(group =>
        group.topics.map(topic => ({
            key: String(topic.topicId),
            label: topic.topicName,
            children: (
                <ul className="ch-lesson-planner-drawer-section-list">
                    {topic.concepts.map(concept => {
                        const added = addedIds.has(String(concept.conceptId));
                        const tags = getConceptTags(concept.conceptId, conceptUsageMap, selectedLessonId);
                        return (
                            <li
                                key={concept.conceptId}
                                className={`ch-lesson-planner-drawer-section-item${added ? ' ch-lesson-planner-drawer-section-item--added' : ''}${concept.disabled ? ' disabled' : ''}`}
                            >
                                <span className={`ch-lesson-planner-drawer-section-dot${added ? ' added' : ''}`} />
                                <span className="ch-lesson-planner-drawer-section-name">{concept.conceptName}</span>
                                {tags.map((tag, i) => <Tag key={i} color={tag.color}>{tag.text}</Tag>)}
                                {added ? (
                                    <Tooltip
                                        title="Already added to this lesson"
                                        disableHoverListener={justAddedIds.has(String(concept.conceptId))}
                                    >
                                        <div
                                            className="ch-lesson-planner-drawer-section-check"
                                            onMouseLeave={() => setJustAddedIds(prev => {
                                                const next = new Set(prev);
                                                next.delete(String(concept.conceptId));
                                                return next;
                                            })}
                                        >
                                            <CheckRounded style={{ fontSize: '18px' }} aria-hidden="true" />
                                        </div>
                                    </Tooltip>
                                ) : (
                                    <Button
                                        type="text"
                                        disabled={concept.disabled}
                                        onClick={(e) => {
                                            const li = e.currentTarget.closest('li');
                                            const id = String(concept.conceptId);
                                            setJustAddedIds(prev => new Set([...prev, id]));
                                            onAdd({ id, title: concept.conceptName });
                                            setTimeout(() => {
                                                li?.nextElementSibling?.querySelector('button')?.focus();
                                            }, 0);
                                        }}
                                        aria-label={`Add ${concept.conceptName}`}
                                    >
                                        <AddRounded style={{ fontSize: '18px' }} />
                                    </Button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            ),
        }))
    );

    return (
        <div className={`ch-lesson-planner-content-panel${open ? ' ch-lesson-planner-content-panel--open' : ''}`}>
            <div className="ch-lesson-planner-content-panel-inner">
                <div className="ch-lesson-planner-content-panel-header">
                    <span className="ch-lesson-planner-content-panel-title">Add lesson content</span>
                    <button
                        className="ch-lesson-planner-content-panel-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <CloseRounded style={{ fontSize: '20px' }} />
                    </button>
                </div>
                <div className="ch-lesson-planner-content-panel-body">
                    {isLoading ? (
                        <div className="ch-lesson-planner-drawer-loader">
                            <CircularLoader />
                        </div>
                    ) : (
                        <Collapse
                            accordion
                            items={collapseItems}
                            className="ch-lesson-planner-content-collapse"
                            expandIcon={({ isActive }) => (
                                <KeyboardArrowDownRounded style={{
                                    fontSize: '18px',
                                    transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s',
                                }} />
                            )}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddContentPanel;
